import type { SchemaType } from '@based/schema'
import { SchemaToType } from './utils'
import { INITIATIVE_USER_ROLES } from './initiativeRoles'

export * from './initiativeRoles'

export const PLOT_STATUS = {
  underOption: 'underOption',
  noPlot: 'noPlot',
  potentialPlotInMind: 'potentialPlotInMind',
} as const
export type PlotStatus = keyof typeof PLOT_STATUS

export const NUMBER_OF_HOMES = {
  lessThan5: 'lessThan5',
  from5To10: 'from5To10',
  from11to30: 'from11to30',
  moreThan30: 'moreThan30',
} as const
export type NumberOfHomes = keyof typeof NUMBER_OF_HOMES

export const initiative = {
  uuid: { type: 'alias', format: 'UUID' },
  slug: { type: 'alias' },
  title: {
    type: 'string',
    title: 'Title of the initiative',
    description: 'this is where you put the title',
  },
  description: {
    type: 'text',
  },
  vision: {
    type: 'text',
  }, // TODO: this can go to content blocks
  contentBlocks: {
    items: {
      ref: 'pageContentBlock',
      prop: 'initiative',
      $position: ['overviewTab'],
      $positionIndex: 'number',
    },
  },
  //uniqueViews: 'cardinality',
  createdAt: {
    type: 'timestamp',
    on: 'create',
  },
  isLive: 'boolean',
  isTest: 'boolean',
  phase: { ref: 'phase', prop: 'initiatives' },
  developmentForm: { ref: 'developmentForm', prop: 'initiatives' },
  searchingForMembers: 'boolean',
  searchingForMembersDescription: 'text', // TODO: this can go to content blocks
  hasAutomaticMemberApproval: 'boolean',
  location: {
    props: {
      geo: {
        props: {
          lat: 'number',
          long: 'number',
        },
      },
      address: {
        props: {
          country: { ref: 'country', prop: 'initiatives' },
          region: { ref: 'region', prop: 'initiatives' },
          municipality: { ref: 'municipality', prop: 'initiatives' },
          locality: { ref: 'locality', prop: 'initiatives' },
          street: 'string',
          postalCode: 'string',
        },
      },
    },
  },
  plotApplications: { items: { ref: 'plotApplication', prop: 'initiative' } },
  plot: { ref: 'plot', prop: 'selectedInitiative' },
  followingPlots: { items: { ref: 'plot', prop: 'followingInitiatives' } },
  updatedAt: {
    type: 'timestamp',
    on: 'update',
  },
  images: { items: { ref: 'file', prop: 'initiativeImage' } },
  users: {
    items: {
      prop: 'initiatives',
      ref: 'user',
      $role: Object.values(INITIATIVE_USER_ROLES),
      $followerSince: { type: 'timestamp' },
      $appliedAt: { type: 'timestamp' },
      $memberSince: { type: 'timestamp' },
      $adminSince: { type: 'timestamp' },
    },
  },
  housingForms: { items: { ref: 'housingForm', prop: 'initiatives' } },
  serviceProviderConcepts: {
    items: { ref: 'serviceProviderConcept', prop: 'initiatives' },
  },
  livingEnvironments: {
    items: { ref: 'livingEnvironment', prop: 'initiatives' },
  },
  targetAudiences: { items: { ref: 'targetAudience', prop: 'initiatives' } },
  interests: { items: { ref: 'interest', prop: 'initiatives' } },
  numberOfHomes: Object.values(NUMBER_OF_HOMES),
  projectSize: 'string',
  buyBudget: { ref: 'buyBudget', prop: 'initiatives' },
  rentalBudget: { ref: 'rentBudget', prop: 'initiatives' },
  potentialPlotStatus: Object.values(PLOT_STATUS),
  potentialPlotAddress: 'string',
  permit: 'boolean',
  financingRequirementsConstruction: 'boolean',
  financingRequirementsPlanning: 'boolean',
  kvkRegistration: 'boolean',
  contactFirstName: 'string',
  contactLastName: 'string',
  contactEmail: { type: 'string', format: 'email' },
  contactPhoneNumber: 'string',
  scheduledCall: 'timestamp',
  isCalendlyScheduled: 'boolean',
  channel: { ref: 'channel', prop: 'initiative' },
  thread: { ref: 'thread', prop: 'initiative' },
  events: { items: { ref: 'event', prop: 'initiative' } },
  serviceProviders: { items: { ref: 'serviceProvider', prop: 'initiatives' } },
  spotlights: { items: { ref: 'spotlight', prop: 'initiative' } },
  amenities: { items: { ref: 'amenity', prop: 'initiatives' } },
  customAmenities: { items: { ref: 'amenity', prop: 'eligibleInitiatives' } },
  plotRegistryApplications: {
    items: { ref: 'plotRegistryApplication', prop: 'initiative' },
  },
  relatedTopicCommunities: {
    items: { ref: 'topicCommunity', prop: 'relatedInitiatives' },
  },
} satisfies SchemaType

export type Initiative = SchemaToType<typeof initiative> & {
  id: number
}
